import { createEffect } from '../utils/create-effect';

import { APP_ELEMENT_ID, SCROLLABLE_INNER_CONTAINER_ID } from './constants';
import { handleFocusElChange } from './handlers/handle-focus-el-change';
import {
  createHighlightsContainerElement,
  createFocusCaretElement,
} from './utils/create-dom-elements';

/**
 * Creates the main effect that draws / renders highlights, focus, etc.
 */
export const createHighlightsEffect = createEffect(() => {
  /**
   * The internal highlights container, where focus, highlights, etc. are drawn and positioned.
   */
  let highlightsContainer: HTMLElement | null = null;

  /**
   * The focus caret element, which moves depending on the focused cell.
   */
  let focusCaret: HTMLElement | null = null;

  /**
   * The mutation observer, which listens to the DOM and waits for the
   * el with the id SCROLLABLE_INNER_CONTAINER_ID to mount. Without this,
   * the highlightsContainer has nothing to attach itself to.
   */
  let observer: MutationObserver | null = null;

  /**
   * The el with the id SCROLLABLE_INNER_CONTAINER_ID, which is the main
   * scrolling container on the databases app, rendered in react.
   */
  let scrollableContainer: HTMLElement | null = null;

  /**
   * We modify the position to be relative when this setup() is run.
   * We cache the original so we can roll it back if there was one.
   */
  let originalPosition: string | null = null;

  function attemptMountingHighlightsContainerToScrollContainer() {
    if (!highlightsContainer) {
      return;
    }

    const scrollableInnerContainer = document.getElementById(
      SCROLLABLE_INNER_CONTAINER_ID
    );

    if (
      scrollableInnerContainer &&
      highlightsContainer.parentElement !== scrollableInnerContainer
    ) {
      originalPosition = scrollableInnerContainer.style.position || null;
      scrollableInnerContainer.style.position = 'relative';
      scrollableInnerContainer.appendChild(highlightsContainer);
      scrollableContainer = scrollableInnerContainer;
    }
  }

  return {
    deltaTypes: ['selectionModified', 'selectionReplaced', 'focusChanged'],

    setup() {
      // create the core elements in memory
      highlightsContainer = createHighlightsContainerElement();
      focusCaret = createFocusCaretElement();
      highlightsContainer.appendChild(focusCaret);

      // setup the MutationObserver to attempt attaching / mounting our highlights container
      observer = new MutationObserver(() =>
        attemptMountingHighlightsContainerToScrollContainer()
      );

      // kick off the observer / watch for changes
      const listenerRoot = document.getElementById(APP_ELEMENT_ID) || document.body;

      observer.observe(listenerRoot, { childList: true, subtree: true });

      // attempt to mount (in case React is already ready)
      attemptMountingHighlightsContainerToScrollContainer();
    },

    dispose() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (highlightsContainer) {
        highlightsContainer.remove();
        highlightsContainer = null;
      }

      if (scrollableContainer) {
        if (originalPosition === null) {
          scrollableContainer.style.removeProperty('position');
        } else {
          scrollableContainer.style.position = originalPosition;
        }

        scrollableContainer = null;
        originalPosition = null;
      }
    },

    onDelta(delta) {
      if (!highlightsContainer || !focusCaret || !scrollableContainer) {
        return;
      }

      if (delta.kind === 'focusChanged') {
        handleFocusElChange(focusCaret, scrollableContainer, delta);

        return;
      }

      if (delta.kind === 'selectionReplaced') {
        if (delta.focusChange) {
          handleFocusElChange(focusCaret, scrollableContainer, delta.focusChange);
        }

        return;
      }
    },
  };
});
